/**
 * Home — long-scroll public landing page for the agency.
 *
 * Section order is driven entirely by `config.homeOrder` (an array of section
 * keys).  Each entry resolves through SECTIONS to a renderer; if the
 * underlying config slice is empty/missing the section returns null and the
 * band silently disappears.  This keeps `/site.config.json` the single
 * switchboard for *what* visitors see and *in what order*.
 *
 * To hide a section: remove its key from `homeOrder`.
 * To reorder: change the array order.
 * To repeat (e.g. CTA twice): list the key twice.
 */
import React from 'react';
import MarketingLayout from './MarketingLayout';
import Hero          from './components/Hero';
import Section       from './components/Section';
import FeatureCard   from './components/FeatureCard';
import TestimonialCard from './components/TestimonialCard';
import FAQItem       from './components/FAQItem';
import CTABand       from './components/CTABand';
import Gallery       from './Gallery';
import useSiteConfig from './useSiteConfig';
import { COLOR, FONT, RADIUS } from '../../components/ss/tokens';

/** Pill chip used by the Service Area and Insurance strips. */
function Chip({ label }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '8px 16px',
        background: '#fff',
        border: `1px solid ${COLOR.blueBorder}`,
        color: COLOR.blue,
        borderRadius: 999,
        fontSize: FONT.size.md,
        fontWeight: FONT.weight.medium,
      }}
    >
      {label}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section renderers — one per `homeOrder` key.                              */
/*                                                                            */
/*  Each renderer receives the full config and returns either JSX or null.    */
/*  Returning null hides the band entirely.                                   */
/* -------------------------------------------------------------------------- */

function renderTrustBar(config) {
  const trustBar = config.trustBar;
  if (!trustBar?.logos?.length) return null;
  return (
    <section
      key="trustBar"
      style={{
        background: '#fff',
        borderBottom: `1px solid ${COLOR.grayBorder}`,
        padding: '36px 24px',
      }}
    >
      <div style={{ maxWidth: '100%', margin: '0 auto', textAlign: 'center' }}>
        {trustBar.headline && (
          <div
            style={{
              fontSize: FONT.size.sm,
              color: COLOR.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '1.4px',
              fontWeight: FONT.weight.semi,
              marginBottom: 18,
            }}
          >
            {trustBar.headline}
          </div>
        )}
        <div style={{ overflow: 'hidden', maskImage: 'linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)', WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent)' }}>
          <div style={{ display: 'inline-flex', gap: 56, paddingRight: 56, animation: 'hhmsMarquee 26s linear infinite', whiteSpace: 'nowrap' }}>
            {[...trustBar.logos, ...trustBar.logos].map((l, i) => (
              <span
                key={i}
                style={{
                  fontSize: FONT.size.lg,
                  fontWeight: FONT.weight.semi,
                  color: COLOR.gray,
                  letterSpacing: '0.4px',
                  opacity: 0.7,
                }}
              >
                {l.name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function renderAbout(config) {
  const about = config.about;
  if (!about) return null;
  return (
    <Section
      key="about"
      id="about"
      background="#fff"
      eyebrow={about.eyebrow}
      title={about.title}
      align="center"
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.4fr 1fr',
          gap: 48,
          alignItems: 'start',
        }}
        className="hhms-about-grid"
      >
        <div data-reveal style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {(Array.isArray(about.body) ? about.body : about.body ? [about.body] : []).map((p, i) => (
            <p
              key={i}
              style={{
                margin: 0,
                fontSize: 16,
                lineHeight: 1.7,
                color: COLOR.textSub,
              }}
            >
              {p}
            </p>
          ))}
        </div>
        {about.stats?.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
            }}
          >
            {about.stats.map((s, i) => (
              <div
                key={i}
                data-reveal
                className="hhms-lift"
                style={{
                  background: COLOR.grayBg,
                  borderRadius: RADIUS.lg,
                  padding: 20,
                  border: `1px solid ${COLOR.grayBorder}`,
                  ['--d']: `${(i % 2) * 90}ms`,
                }}
              >
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: FONT.weight.xbold,
                    color: COLOR.blue,
                    letterSpacing: '-0.5px',
                  }}
                >
                  {s.value}
                </div>
                <div style={{ marginTop: 4, fontSize: FONT.size.sm, color: COLOR.textMuted, lineHeight: 1.4 }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <style>{`
        @media (max-width: 820px) {
          .hhms-about-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </Section>
  );
}

function renderServices(config) {
  const features = config.features;
  if (!features?.items?.length) return null;
  return (
    <Section
      key="services"
      id="services"
      background={COLOR.grayBg}
      eyebrow={features.eyebrow}
      title={features.title}
      subtitle={features.subtitle}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 20,
        }}
      >
        {features.items.map((f, i) => (
          <FeatureCard key={i} index={i} icon={f.icon} title={f.title} body={f.body} />
        ))}
      </div>
    </Section>
  );
}

function renderHowItWorks(config) {
  const howItWorks = config.howItWorks;
  if (!howItWorks?.steps?.length) return null;
  const stepLabel = config.ui?.stepLabel || 'STEP';
  return (
    <Section
      key="howItWorks"
      id="how-it-works"
      background="#fff"
      eyebrow={howItWorks.eyebrow}
      title={howItWorks.title}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 20,
        }}
      >
        {howItWorks.steps.map((s, i) => (
          <div
            key={i}
            data-reveal
            className="hhms-lift"
            style={{
              border: `1px solid ${COLOR.grayBorder}`,
              borderRadius: RADIUS.lg,
              padding: 24,
              background: '#fff',
              position: 'relative',
              ['--d']: `${(i % 4) * 90}ms`,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: FONT.weight.bold,
                color: COLOR.blue,
                letterSpacing: '2px',
              }}
            >
              {stepLabel} {s.number}
            </div>
            <h3
              style={{
                margin: '8px 0 10px',
                fontSize: 18,
                fontWeight: FONT.weight.bold,
                color: COLOR.text,
              }}
            >
              {s.title}
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: FONT.size.md,
                lineHeight: 1.6,
                color: COLOR.textMuted,
              }}
            >
              {s.body}
            </p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function renderWhyUs(config) {
  const whyUs = config.whyUs;
  if (!whyUs?.items?.length) return null;
  return (
    <Section
      key="whyUs"
      id="why"
      background={COLOR.grayBg}
      eyebrow={whyUs.eyebrow}
      title={whyUs.title}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 20,
        }}
      >
        {whyUs.items.map((f, i) => (
          <FeatureCard key={i} index={i} icon={f.icon} title={f.title} body={f.body} />
        ))}
      </div>
    </Section>
  );
}

function renderTestimonials(config) {
  const testimonials = config.testimonials;
  if (!testimonials?.items?.length) return null;
  return (
    <Section
      key="testimonials"
      id="testimonials"
      background="#fff"
      eyebrow={testimonials.eyebrow}
      title={testimonials.title}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 20,
        }}
      >
        {testimonials.items.map((t, i) => (
          <TestimonialCard key={i} quote={t.quote} author={t.author} role={t.role} />
        ))}
      </div>
    </Section>
  );
}

function renderServiceArea(config) {
  const serviceArea = config.serviceArea;
  if (!serviceArea?.regions?.length) return null;
  return (
    <Section
      key="serviceArea"
      id="service-area"
      background={COLOR.grayBg}
      eyebrow={serviceArea.eyebrow}
      title={serviceArea.title}
      subtitle={serviceArea.subtitle}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          justifyContent: 'center',
          maxWidth: '100%',
          margin: '0 auto',
        }}
      >
        {serviceArea.regions.map((r, i) => (
          <Chip key={i} label={r} />
        ))}
      </div>
    </Section>
  );
}

function renderInsurance(config) {
  const insurance = config.insurance;
  if (!insurance?.carriers?.length) return null;
  return (
    <Section
      key="insurance"
      id="insurance"
      background="#fff"
      eyebrow={insurance.eyebrow}
      title={insurance.title}
      subtitle={insurance.subtitle}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          justifyContent: 'center',
          maxWidth: '100%',
          margin: '0 auto',
        }}
      >
        {insurance.carriers.map((c, i) => (
          <Chip key={i} label={c} />
        ))}
      </div>
    </Section>
  );
}

function renderValues(config) {
  const values = config.values;
  if (!values?.items?.length) return null;
  return (
    <Section
      key="values"
      id="values"
      background="#fff"
      eyebrow={values.eyebrow}
      title={values.title}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 20,
        }}
      >
        {values.items.map((v, i) => (
          <FeatureCard key={i} index={i} icon={v.icon} title={v.title} body={v.body} />
        ))}
      </div>
    </Section>
  );
}

function renderTeam(config) {
  const team = config.team;
  if (!team?.members?.length) return null;
  return (
    <Section
      key="team"
      id="team"
      background={COLOR.grayBg}
      eyebrow={team.eyebrow}
      title={team.title}
      subtitle={team.subtitle}
      align="center"
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 20,
          maxWidth: '100%',
          margin: '0 auto',
        }}
      >
        {team.members.map((m, i) => (
          <div
            key={i}
            style={{
              background: '#fff',
              border: `1px solid ${COLOR.grayBorder}`,
              borderRadius: RADIUS.lg,
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 10,
            }}
          >
            {/* Avatar circle */}
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: COLOR.blue,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                fontWeight: FONT.weight.bold,
                flexShrink: 0,
              }}
            >
              {m.initials || (m.name || '').split(' ').map(w => w[0]).join('').slice(0, 2)}
            </div>
            <div>
              <div
                style={{
                  fontSize: 17,
                  fontWeight: FONT.weight.bold,
                  color: COLOR.text,
                  lineHeight: 1.3,
                }}
              >
                {m.name}
              </div>
              <div
                style={{
                  fontSize: FONT.size.sm,
                  fontWeight: FONT.weight.semi,
                  color: COLOR.blue,
                  marginTop: 2,
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                }}
              >
                {m.role}
              </div>
            </div>
            <p
              style={{
                margin: 0,
                fontSize: FONT.size.md,
                lineHeight: 1.6,
                color: COLOR.textMuted,
              }}
            >
              {m.bio}
            </p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function renderFaq(config) {
  const faq = config.faq;
  if (!faq?.items?.length) return null;
  return (
    <Section
      key="faq"
      id="faq"
      background={COLOR.grayBg}
      eyebrow={faq.eyebrow}
      title={faq.title}
      innerMaxWidth={820}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {faq.items.map((item, i) => (
          <FAQItem key={i} q={item.q} a={item.a} defaultOpen={i === 0} />
        ))}
      </div>
    </Section>
  );
}

function renderCtaBand() {
  // CTABand reads its own slice from config; it returns null if missing.
  return <CTABand key="ctaBand" />;
}

const SECTIONS = {
  trustBar:     renderTrustBar,
  about:        renderAbout,
  services:     renderServices,
  values:       renderValues,
  howItWorks:   renderHowItWorks,
  whyUs:        renderWhyUs,
  team:         renderTeam,
  testimonials: renderTestimonials,
  serviceArea:  renderServiceArea,
  insurance:    renderInsurance,
  faq:          renderFaq,
  ctaBand:      renderCtaBand,
};

const FALLBACK_ORDER = [
  'trustBar', 'about', 'services', 'values', 'howItWorks', 'whyUs',
  'team', 'testimonials', 'serviceArea', 'insurance', 'faq', 'ctaBand',
];

export default function Home() {
  const { config } = useSiteConfig();
  const order = Array.isArray(config.homeOrder) && config.homeOrder.length > 0
    ? config.homeOrder
    : FALLBACK_ORDER;

  return (
    <MarketingLayout>
      <Hero />
      {order.map((key, i) => {
        const renderer = SECTIONS[key];
        if (!renderer) return null;
        const node = renderer(config);
        if (!node) return null;
        // Wrap with a stable key in case the same section appears twice
        // (e.g. ctaBand listed multiple times).
        return React.cloneElement(node, { key: `${key}-${i}` });
      })}
      {/* Live media gallery — always rendered; hides itself when empty */}
      <Gallery config={config} />
    </MarketingLayout>
  );
}
